'use client';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { FeaturesListType } from '@/types/ui';
import { Thunder, Shield, Phone } from '@/public/images/icons/icons';
import { fadeInUp, viewportOnce } from '@/lib/animations';


export default function Hero() {
  const t = useTranslations('Hero');

  const features: FeaturesListType[] = [
    {
      title: 'instant',
      description: 'instant_desc',
            icon: <Thunder width={30} height={30} className="shrink-0" />,
    },
    {
      title: 'secure',
      description: 'secure_desc',
            icon: <Shield width={30} height={30} className="shrink-0" />,
    },
    {
      title: 'simple',
      description: 'simple_desc',
            icon: <Phone width={30} height={30} className="shrink-0" />,
    },
  ];

  return (
    <motion.section
      className="relative w-full bg-white overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0 items-center px-6 sm:px-10 lg:px-16">
        <div className="lg:col-span-6 flex flex-col space-y-8 py-12 lg:py-20">
          <span className="hero-fade-up text-xs sm:text-sm font-semibold tracking-widest color-primary uppercase" style={{ animationDelay: '0ms' }}>
            {t('main_subtitle')}
          </span>
          <h1 className="hero-fade-up text-4xl sm:text-6xl lg:text-7xl font-semibold text-black tracking-tight leading-[1.05]" style={{ animationDelay: '120ms' }}>
            {t('main_title')}<br />
            <span className="color-primary">Kuik</span>ly.
          </h1>
          <p className="hero-fade-up text-base sm:text-lg text-primary leading-relaxed font-normal max-w-xl" style={{ animationDelay: '240ms' }}>
            {t('subtitle')}
          </p>
          <div className="hero-fade-up flex flex-wrap items-center gap-4 pt-2" style={{ animationDelay: '360ms' }}>
            <Link
              href="#how_it_works"
              className="btn-blink-hover inline-flex items-center justify-center gap-2 bg-primary text-white text-sm font-bold px-7 py-3.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
            >
              <span>{t('how_it_works')}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="hero-fade-up grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4" style={{ animationDelay: '480ms' }}>
            {features.map((feature: FeaturesListType, index: number) => (
              <div key={feature.title} className="flex items-start space-x-3">
                {feature.icon && (
                  <div className={`p-1.5 bg-blue-50 rounded-lg shrink-0 mt-0.5${index === 1 ? ' shield-flip-wrap' : ''}`}>
                    {feature.icon}
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-bold color-primary">{t(feature.title)}</h4>
                  <p className="text-xs text-gray-500 font-medium">{t(feature.description)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="hero-fade-up space-y-3 pt-2" style={{ animationDelay: '600ms' }}>
            <span className="text-xs font-semibold color-primary">
              {t('available_banks')}
            </span>
            <div className="flex items-center gap-3 flex-wrap mt-2">
              <div className="border border-gray-100 px-3 py-2 rounded-lg flex items-center justify-center gap-1.5">
                <Image src="/images/flags/al.svg" alt="Albania flag" width={16} height={16} className="rounded-sm shrink-0" />
                <span className=" text-gray-500 font-medium text-xs tracking-tighter">Raiffeisen Bank Albania</span>
              </div>
              <div className="border border-gray-100 px-3 py-2 rounded-lg flex items-center justify-center gap-1.5">
                <Image src="/images/flags/xk.svg" alt="Kosovo flag" width={16} height={16} className="rounded-sm shrink-0" />
                <span className=" text-gray-500 font-medium text-xs tracking-tighter">Raiffeisen Bank Kosovo</span>
              </div>
              <span className="text-xs text-gray-400 font-medium ml-1 inline-flex items-center gap-0.5">
                {t('more_banks_soon').replace(/\.+$/, '')}
                <span className="inline-flex ml-0.5">
                  <span className="dot-blink" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="dot-blink" style={{ animationDelay: '200ms' }}>.</span>
                  <span className="dot-blink" style={{ animationDelay: '400ms' }}>.</span>
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 relative flex justify-center items-start ">
          <div className="hero-image-float relative w-full  aspect-[4/3] lg:aspect-square flex items-start justify-center">
            <Image
              src="/images/homepage/hero.webp"
              alt="Kuik App phone interface and frog mascot"
              fill
              priority
              className="object-contain"
            />
          </div>
        </div>
      </div>
    </motion.section>
  );
}